<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')"></b-form-input>
            <form-select :model="filterFormModel" attribute="active" :options="[
              {value: null, text: $t('status')},
              {value: 'activated', text: $t('activated')},
              {value: 'deactivated', text: $t('deactivated')}
            ]" class="mr-1 mb-1"></form-select>
            <form-select :model="filterFormModel" attribute="category"
                         :options="[{value: null, text: $t('category')}].concat(categoryOptions)"
                         class="mr-1 mb-1"></form-select>
            <admin-user-select class="mr-1 mb-1" :model="filterFormModel" attribute="createdBy"
                               :place-holder="$t('createdBy')"></admin-user-select>
            <time-range-picker :model="filterFormModel" :label="$t('createTime')" class="mr-1 mb-1"></time-range-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting"
             v-if="['owner', 'admin', 'tech', 'operationsManager', 'manager'].includes($store.state.user.role)">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-1"></div>
            <div class="col-lg-3">{{ $t('info') }}</div>
            <div class="col-lg-3">{{ $t('price') }}</div>
            <div class="col-lg-3">{{ $t('time') }}</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <div class="col-lg-1">
              <figure class="c-board-table__avatar border-0">
                <img v-if="item.images && item.images.length" :src="item.images[0].url" class="thumbnail" alt="logo"/>
                <img v-else src="/img/default-avatar.png" class="thumbnail" alt="logo"/>
              </figure>
            </div>
            <div class="col-lg-3">
              <p>
                <b>{{ $t('productName') }}: </b><span>{{ item.name }}</span>
              </p>
              <p>
                <b>{{ $t('productCode') }}: </b><span>{{ item.sku }}</span>
              </p>
              <p>
                <b>{{ $t('category') }}: </b>
                <span v-if="item.categories && item.categories.length">{{ getCategoryText(item.categories) }}</span>
                <span v-else class="text-secondary">{{ $t('notUpdated') }}</span>
              </p>
            </div>
            <div class="col-lg-3">
              <p>
                <b>{{ $t('listPrice') }}: </b><span>{{ item.listPrice | numFormat }}&nbsp;&#8363;</span>
              </p>
              <p>
                <b>{{ $t('sellPrice') }}: </b><span>{{ item.sellPrice | numFormat }}&nbsp;&#8363;</span>
              </p>
            </div>
            <div class="col-lg-3">
              <p>
                <b>{{ $t('createTime') }}: </b><span>{{ item.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
              </p>
              <p>
                <b>{{ $t('createdBy') }}: </b>
                <span v-if="item.createdBy">{{ item.createdBy.name }}</span>
                <span v-else class="text-secondary">{{ $t('notUpdated') }}</span>
              </p>
              <p>
                <b>{{ $t('updateTime') }}: </b>
                <span v-if="item.updateTime">{{ item.updateTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
                <span v-else class="text-secondary">{{ $t('notUpdated') }}</span>
              </p>
              <p>
                <b>{{ $t('updatedBy') }}: </b>
                <span v-if="item.updatedBy">{{ item.updatedBy.name }}</span>
                <span v-else class="text-secondary">{{ $t('notUpdated') }}</span>
              </p>
              <div>
                <label v-if="item.active" class="badge badge-success">{{ $t('activated') }}</label>
                <label v-else class="badge badge-danger">{{ $t('deactivated') }}</label>
              </div>
            </div>
            <div class="col-lg-2">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix is-2">
                  <div class="c-buttons__col"
                       v-if="['owner', 'admin', 'tech', 'operationsManager', 'manager', 'accountant'].includes($store.state.user.role)">
                    <button @click="update(item._id)" type="button" class="btn btn-sm btn-info mr-1">
                      <font-awesome-icon icon="edit"/>&nbsp;{{ $t('edit') }}
                    </button>
                    <button @click="del(item._id)" type="button"
                            v-if="['owner', 'admin', 'tech', 'operationsManager', 'manager'].includes($store.state.user.role)"
                            class="btn btn-sm btn-danger mr-1">
                      <font-awesome-icon icon="trash"/>&nbsp;{{ $t('delete') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length">
        <span v-if="filterFormModel.page === 1 && !filterFormModel.keyword">{{ $t('notFoundCreate') }}</span>
        <span v-if="filterFormModel.page > 1 || filterFormModel.keyword">{{ $t('notFoundSearch') }}</span>
      </div>

      <div class="col-12" v-if="itemCount">
        <div class="c-paging__count" v-if="itemCount">
          {{ $t('found', [itemCount]) }}
        </div>
        <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                      v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                      @input="load">
        </b-pagination>
      </div>
    </div>
    <b-modal v-model="showForm" size="lg" :title="formTitle" @ok="save" no-close-on-backdrop no-enforce-focus>
      <div class="row">
        <div class="col-md-12">
          <form-input :label="$t('name')" :model="formModel" attribute="name" :errors="formErrors"></form-input>
          <form-input :disabled="formModel.id" :label="$t('code')" :model="formModel" attribute="sku"
                      :errors="formErrors"></form-input>
          <form-input :label="$t('listPrice')" :model="formModel" attribute="listPrice"
                      :errors="formErrors"></form-input>
          <form-input :label="$t('sellPrice')" :model="formModel" attribute="sellPrice"
                      :errors="formErrors"></form-input>
          <form-textarea :label="$t('desc')" :model="formModel" attribute="desc" :errors="formErrors"></form-textarea>
          <form-checkbox-group :model="formModel" :label="$t('category')" attribute="categories" :stacked="true"
                               :errors="formErrors" :options="categoryOptions">
          </form-checkbox-group>
          <div>
            <label>{{ $t('image') }}</label>
            <div class="mb-2 mt-2" v-if="formModel.images && formModel.images.length && !imageLoading">
              <img v-for="(image, index) in formModel.images" :key="index" alt="img" style="max-height: 180px"
                   class="img-thumbnail mr-2" :src="image.url">
            </div>
            <form-multiple-file service="/upload/admin-product" :model="formModel"
                                attribute="images" :errors="formErrors"
                                accept="image/jpeg,image/jpg,image/png,application/pdf">
            </form-multiple-file>
          </div>
          <form-checkbox :chk-label="$t('activated')" :model="formModel" attribute="active" :errors="formErrors"/>
        </div>
      </div>

      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="showForm = false">{{ $t('cancel') }}</button>
        <button class="btn btn-primary" @click="save"
                :disabled="!['owner', 'admin', 'tech', 'operationsManager', 'manager'].includes($store.state.user.role)">
          {{ $t('save') }}
        </button>
      </template>
    </b-modal>
  </main>
</template>

<script>
import List from '../../mixins/List'
import CRUD from '../../mixins/CRUD'
import Utils from '../../utils'
import FormMultipleFile from '@/components/FormMultipleFile.vue'
import AdminUserSelect from '@/components/AdminUserSelect.vue'
import TimeRangePicker from '@/components/TimeRangePicker.vue'

export default {
  components: {TimeRangePicker, AdminUserSelect, FormMultipleFile},
  mixins: [List, CRUD],
  data: function () {
    return {
      service: '/admin-product',
      autoLoad: true,
      defaultFilterFormModel: {
        keyword: null,
        active: null,
        createdBy: null,
        category: null
      },
      defaultFormModel: {
        name: null,
        sku: null,
        listPrice: null,
        sellPrice: null,
        desc: null,
        categories: [],
        active: true,
        images: []
      },
      imageLoading: false,
      categoryOptions: []
    }
  },
  methods: {
    async getCategoryOptions() {
      await this.$bgService.get(this.service + '/categories').then(response => {
        if (response.data.length) {
          this.categoryOptions = response.data
        }
      })
    },
    getCategoryText(categories) {
      return this.categoryOptions
          .filter(category => categories.includes(category.value))
          .map(category => category.text).join(', ')
    }
  },
  i18n: {
    messages: {
      en: {
        productName: 'Product name',
        productCode: 'Product code',
        listPrice: 'Old price',
        sellPrice: 'Sell price',
        category: 'Category',
        image: 'Image',
        createdBy: 'Created by',
        updatedBy: 'Updated by',
        code: 'Code'
      },
      vi: {
        productName: 'Tên sản phẩm',
        productCode: 'Mã sản phẩm',
        listPrice: 'Giá cũ',
        sellPrice: 'Giá bán',
        category: 'Danh mục',
        image: 'Ảnh',
        createdBy: 'Tạo bởi',
        updatedBy: 'Cập nhật bởi',
        code: 'Mã'
      }
    }
  },
  watch: {
    'formModel.name': {
      handler(val) {
        if (!this.formModel.id) {
          if (val) {
            this.formModel.sku = Utils.methods.createAlias(val)
          } else {
            this.formModel.sku = null
          }
        }
      }
    }
  },
  mounted() {
    this.getCategoryOptions()
  }
}
</script>